import { FLOW_DEFAULTS } from '../sim/avalanche/params.ts';
import type { ReleaseDepthMode } from '../sim/avalanche/release.ts';
import type { RunoutStats } from '../sim/avalanche/flow.ts';
import { GRAIN_LABELS } from '../sim/snowpack.ts';
import {
  armCrossSection,
  armTrigger,
  clearCrossSection,
  clearTriggerQueue,
  crossSectionArmed,
  crossSectionLine,
  crossSectionPending,
  disarmCrossSection,
  disarmTrigger,
  hoverRelease,
  runs,
  runStatus,
  selectRun,
  selectedRun,
  selectedRunId,
  triggerArmed,
  triggerMessage,
  triggerQueue,
  triggerSettings,
  undoLastRun,
  type TriggerSettings,
} from '../state/avalanche.ts';
import { formatNumber, formatSigned } from './format.ts';
import { NumberField } from './NumberField.tsx';
import { SpeedPresetButtons } from './SpeedPresetButtons.tsx';

function updateTrigger(patch: Partial<TriggerSettings>): void {
  triggerSettings.value = { ...triggerSettings.value, ...patch };
}

const DEPTH_MODE_LABELS: Record<ReleaseDepthMode, string> = {
  slab: 'Slab above weakest layer',
  newSnow: 'New snow only',
  full: 'Full depth',
  fixed: 'Fixed depth',
};

function TriggerPreviewReadout() {
  const rel = hoverRelease.value;
  if (!triggerArmed.value) return null;
  if (!rel) return <p class="hint">Hover the terrain to preview a release.</p>;
  if (rel.cells.length === 0) {
    return <p class="warning">{rel.reason ?? 'Nothing releases here.'}</p>;
  }
  return (
    <dl class="info-list">
      <dt>Area</dt>
      <dd>{formatNumber(rel.areaM2)} m²</dd>
      <dt>Volume</dt>
      <dd>{formatNumber(rel.volumeM3)} m³</dd>
      <dt>Mass</dt>
      <dd>{formatNumber(rel.massKg / 1000, 1)} t</dd>
      <dt>Mean depth</dt>
      <dd>{formatNumber(rel.meanDepth * 100)} cm</dd>
      <dt>Size class</dt>
      <dd>{rel.sizeClass}</dd>
      <dt>Snow type</dt>
      <dd>{rel.loose ? `${rel.flowType} loose` : rel.flowType}</dd>
      {rel.failureGrain !== null ? (
        <>
          <dt>Failure layer</dt>
          <dd>{GRAIN_LABELS[rel.failureGrain]}</dd>
        </>
      ) : null}
    </dl>
  );
}

function RunStatusSection() {
  const status = runStatus.value;
  const queue = triggerQueue.value;
  const msg = triggerMessage.value;
  return (
    <>
      {status.kind === 'running' ? (
        <div class="scenario-status">
          <progress />
          <span>
            Simulating… t = {formatNumber(status.simTime, 1)} s, {formatNumber(status.moving)}{' '}
            particles moving
          </span>
        </div>
      ) : null}
      {status.kind === 'error' ? (
        <div class="scenario-status error">Avalanche failed: {status.message}</div>
      ) : null}
      {queue.length > 0 ? (
        <div class="scenario-status">
          <span>{queue.length} queued</span>
          <button type="button" class="icon-button" onClick={clearTriggerQueue}>
            Clear queue
          </button>
        </div>
      ) : null}
      {msg ? <p class="warning">{msg}</p> : null}
    </>
  );
}

function runoutVerdict(runout: RunoutStats, leftArea: boolean): string {
  if (leftArea) {
    return 'Part of the avalanche left the loaded area, so it ran further than shown — load a larger area to compare with the α–β model.';
  }
  if (runout.betaDeg === null || runout.predictedAlphaDeg === null) {
    return 'The flow never flattened below 10° — no α–β prediction.';
  }
  const SD = 2.3;
  const diff = runout.alphaDeg - runout.predictedAlphaDeg;
  if (diff > SD) {
    // The regression describes extreme (~100-year) runouts; single events often stop shorter.
    return `Stopped shorter than the Norwegian α–β model's extreme runout (${formatNumber(diff / SD, 1)} SD) — common for a single event.`;
  }
  if (diff < -SD) {
    return `Ran further than the Norwegian α–β model predicts (${formatNumber(-diff / SD, 1)} SD).`;
  }
  return 'Within 1 SD of the Norwegian α–β model.';
}

function TotalsSection() {
  const run = selectedRun.value;
  if (!run) return null;
  const s = run.result.stats;
  const coarsened = run.params.particleMass > run.requestedParticleMass * 1.001;
  return (
    <section class="panel-section">
      <h3 class="segmented-heading">Totals — run #{run.id}</h3>
      <dl class="info-list">
        <dt>Released</dt>
        <dd>
          {formatNumber(s.releasedKg / 1000, 1)} t / {formatNumber(s.releaseVolumeM3)} m³
        </dd>
        <dt>Entrained</dt>
        <dd>{formatNumber(s.entrainedKg / 1000, 1)} t</dd>
        <dt>Deposited</dt>
        <dd>{formatNumber(s.depositedKg / 1000, 1)} t</dd>
        {s.detrainedKg > 0 ? (
          <>
            <dt>…held back by forest</dt>
            <dd>{formatNumber(s.detrainedKg / 1000, 1)} t</dd>
          </>
        ) : null}
        <dt>Outflow (left the area)</dt>
        <dd>{formatNumber(s.outflowKg / 1000, 1)} t</dd>
        <dt>Mass balance</dt>
        <dd>{formatSigned(s.balanceKg / 1000, 2)} t</dd>
        <dt>Deposit area</dt>
        <dd>{formatNumber(s.depositAreaM2)} m²</dd>
        <dt>Max / mean deposit depth</dt>
        <dd>
          {formatNumber(s.maxDepositDepth * 100)} / {formatNumber(s.meanDepositDepth * 100)} cm
        </dd>
        <dt>Deposit volume</dt>
        <dd>{formatNumber(s.depositVolumeM3)} m³</dd>
        <dt>EAWS size</dt>
        <dd>{s.sizeClass}</dd>
        <dt>Max speed</dt>
        <dd>{formatNumber(s.maxSpeed, 1)} m/s</dd>
        <dt>Max pressure</dt>
        <dd>{formatNumber(s.maxPressureKPa, 1)} kPa</dd>
        <dt>Duration</dt>
        <dd>{formatNumber(s.durationS, 0)} s</dd>
        <dt>Particles</dt>
        <dd>
          {formatNumber(s.particles.initial)} → {formatNumber(s.particles.peak)} (
          {formatNumber(s.particles.massKg, 1)} kg each{coarsened ? ', coarsened' : ''})
        </dd>
        <dt>Compute time</dt>
        <dd>{formatNumber(run.computeMs, 0)} ms</dd>
        <dt>Flow type</dt>
        <dd>
          {run.params.flowType} (μ {formatNumber(run.params.mu, 3)}, ξ{' '}
          {formatNumber(run.params.xi, 0)})
        </dd>
      </dl>
      {s.runout ? (
        <>
          <dl class="info-list">
            <dt>α (top → tip)</dt>
            <dd>{formatNumber(s.runout.alphaDeg, 1)}°</dd>
            {s.runout.betaDeg !== null ? (
              <>
                <dt>β</dt>
                <dd>{formatNumber(s.runout.betaDeg, 1)}°</dd>
              </>
            ) : null}
            {s.runout.predictedAlphaDeg !== null ? (
              <>
                <dt>Predicted α</dt>
                <dd>{formatNumber(s.runout.predictedAlphaDeg, 1)}° ± 2.3°</dd>
              </>
            ) : null}
            <dt>Reach</dt>
            <dd>{formatNumber(s.runout.reach)} m</dd>
            <dt>Fall height</dt>
            <dd>{formatNumber(s.runout.fallHeight)} m</dd>
          </dl>
          <p class="hint">{runoutVerdict(s.runout, s.leftArea)}</p>
        </>
      ) : null}
      {run.result.frames.frames.length === 0 ? (
        <p class="hint">Replay not kept for this run (only the 3 most recent keep frames).</p>
      ) : null}
    </section>
  );
}

function RunHistorySection() {
  const list = runs.value;
  if (list.length === 0) return null;
  const selectedId = selectedRunId.value ?? list[list.length - 1]!.id;
  const busy = runStatus.value.kind === 'running';
  return (
    <section class="panel-section">
      <h3 class="segmented-heading">Run history</h3>
      <ul class="run-history-list">
        {list.map((run) => {
          const s = run.result.stats;
          return (
            <li key={run.id}>
              <button
                type="button"
                class={'run-history-item' + (run.id === selectedId ? ' active' : '')}
                onClick={() => selectRun(run.id)}
              >
                <span>
                  #{run.id} · {run.trigger.kind} · size {s.sizeClass}
                  {run.result.frames.frames.length === 0 ? ' · no replay' : ''}
                </span>
                <span>
                  {formatNumber(s.depositedKg / 1000, 1)} t
                  {s.runout ? ` · α ${formatNumber(s.runout.alphaDeg, 1)}°` : ''}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <button type="button" class="icon-button" onClick={undoLastRun} disabled={busy}>
        Undo last run
      </button>
    </section>
  );
}

function CrossSectionSection() {
  const armed = crossSectionArmed.value;
  const pending = crossSectionPending.value;
  const line = crossSectionLine.value;
  return (
    <section class="panel-section">
      <h3 class="segmented-heading">Cross-section</h3>
      <button
        type="button"
        class={armed ? 'active' : ''}
        onClick={() => (armed ? disarmCrossSection() : armCrossSection())}
      >
        {armed ? (pending ? 'Click the second point…' : 'Click the first point…') : 'Cross-section'}
      </button>
      {line ? (
        <button type="button" class="icon-button" onClick={clearCrossSection}>
          Clear
        </button>
      ) : null}
      <p class="hint">Click two points on the terrain (not while the trigger is armed).</p>
    </section>
  );
}

/** Avalanche section of the scene panel: trigger tool, run status, totals for the selected run,
 * run history with undo, and the cross-section tool. Sits at the top of the scene controls. */
export function AvalanchePanel() {
  const settings = triggerSettings.value;
  const armed = triggerArmed.value;

  return (
    <>
      <section class="panel-section avalanche-panel">
        <h2>Avalanche</h2>
        <button
          type="button"
          class={'primary trigger-arm-button' + (armed ? ' active' : '')}
          onClick={() => (armed ? disarmTrigger() : armTrigger())}
        >
          Click-to-release: {armed ? 'on' : 'off'}
        </button>
        {armed ? <p class="hint">Stays on until you turn it off again (or press Esc).</p> : null}

        <div class="field-row">
          <span class="field-label">Playback speed</span>
        </div>
        <SpeedPresetButtons />

        {armed ? (
          <>
            <div class="segmented">
              <button
                class={settings.mode === 'brush' ? 'active' : ''}
                onClick={() => updateTrigger({ mode: 'brush' })}
              >
                Brush
              </button>
              <button
                class={settings.mode === 'natural' ? 'active' : ''}
                onClick={() => updateTrigger({ mode: 'natural' })}
              >
                Natural trigger
              </button>
            </div>

            {settings.mode === 'brush' ? (
              <>
                <NumberField
                  label="Brush radius"
                  value={settings.radius}
                  min={10}
                  max={300}
                  step={5}
                  unit="m"
                  onChange={(v) => updateTrigger({ radius: v })}
                />
                <div class="field-row">
                  <span class="field-label">Release depth</span>
                  <select
                    value={settings.depthMode}
                    onChange={(e) =>
                      updateTrigger({
                        depthMode: (e.target as HTMLSelectElement).value as ReleaseDepthMode,
                      })
                    }
                  >
                    {(Object.keys(DEPTH_MODE_LABELS) as ReleaseDepthMode[]).map((m) => (
                      <option key={m} value={m}>
                        {DEPTH_MODE_LABELS[m]}
                      </option>
                    ))}
                  </select>
                </div>
                {settings.depthMode === 'fixed' ? (
                  <NumberField
                    label="Fixed depth"
                    value={settings.fixedCm}
                    min={5}
                    max={500}
                    step={5}
                    unit="cm"
                    onChange={(v) => updateTrigger({ fixedCm: v })}
                  />
                ) : null}
              </>
            ) : (
              <NumberField
                label="Max fracture reach"
                value={settings.maxPropagation}
                min={50}
                max={1000}
                step={10}
                unit="m"
                onChange={(v) => updateTrigger({ maxPropagation: v })}
              />
            )}

            <div class="field-row">
              <span class="field-label">Particle size</span>
              <select
                value={settings.particleMassKg === null ? 'auto' : 'manual'}
                onChange={(e) => {
                  const v = (e.target as HTMLSelectElement).value;
                  updateTrigger({
                    particleMassKg:
                      v === 'auto' ? null : (settings.particleMassKg ?? FLOW_DEFAULTS.particleMass),
                  });
                }}
              >
                <option value="auto">
                  Auto (0.25 m³ / {FLOW_DEFAULTS.particleMass} kg per particle)
                </option>
                <option value="manual">Manual</option>
              </select>
            </div>
            {settings.particleMassKg !== null ? (
              <NumberField
                label="Particle mass"
                value={settings.particleMassKg}
                min={1}
                max={5000}
                step={1}
                unit="kg"
                onChange={(v) => updateTrigger({ particleMassKg: v })}
              />
            ) : null}

            <TriggerPreviewReadout />
          </>
        ) : null}

        <RunStatusSection />
      </section>

      <TotalsSection />
      <RunHistorySection />
      <CrossSectionSection />
    </>
  );
}
